import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Inbox, Filter, ChevronDown, ExternalLink } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ service: "", lang: "", source: "" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/leads", { params: { ...filters, limit: 100 } })
      .then((r) => setLeads(r.data?.leads || []))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
    adminApi.get("/admin/leads/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(load, [JSON.stringify(filters)]); // eslint-disable-line

  const services = useMemo(
    () => Array.from(new Set(leads.map((l) => l.service))).filter(Boolean), [leads]);

  return (
    <div data-testid="admin-leads-page" className="space-y-8">
      <Helmet><title>Lead'ler · Admin</title></Helmet>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-brand-pink-deep font-bold">CRM</span>
        <h1 className="mt-2 font-display text-display-2 font-bold text-ink">Lead Inbox</h1>
      </div>

      {/* Attribution analytics */}
      {analytics ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-mist bg-white p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Top Source</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {(analytics.top_sources || []).slice(0, 4).map((s) => (
                <li key={s.key} className="flex items-center justify-between">
                  <span className="text-ink/80 truncate">{s.key || "(direct)"}</span>
                  <span className="font-bold text-brand-pink-deep">{s.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-mist bg-white p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Top Service</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {(analytics.top_services || []).slice(0, 4).map((s) => (
                <li key={s.key} className="flex items-center justify-between">
                  <span className="text-ink/80 truncate">{s.key}</span>
                  <span className="font-bold text-brand-pink-deep">{s.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-mist bg-white p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Top Landing Page</p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {(analytics.top_pages || []).slice(0, 4).map((p) => (
                <li key={p.key} className="flex items-center justify-between gap-3">
                  <span className="text-ink/80 truncate font-mono text-xs">{p.key}</span>
                  <span className="font-bold text-brand-pink-deep flex-shrink-0">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-mist bg-white p-4">
        <Filter className="h-4 w-4 text-ink/50" />
        <select value={filters.service} onChange={(e) => setFilters((f) => ({ ...f, service: e.target.value }))}
          data-testid="lead-filter-service"
          className="rounded-xl border border-mist bg-cream px-3 py-1.5 text-sm">
          <option value="">Tüm hizmetler</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.lang} onChange={(e) => setFilters((f) => ({ ...f, lang: e.target.value }))}
          data-testid="lead-filter-lang"
          className="rounded-xl border border-mist bg-cream px-3 py-1.5 text-sm">
          <option value="">Tüm diller</option>
          <option value="tr">TR</option><option value="en">EN</option>
          <option value="es">ES</option><option value="de">DE</option>
          <option value="fr">FR</option><option value="ar">AR</option>
        </select>
        <input value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          placeholder="UTM source filter" data-testid="lead-filter-source"
          className="rounded-xl border border-mist bg-cream px-3 py-1.5 text-sm flex-1 min-w-[160px]" />
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-mist bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink/55 text-sm">Yükleniyor…</div>
        ) : err ? (
          <div className="p-10 text-center text-red-700 text-sm">{err}</div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center text-ink/55 text-sm">
            <Inbox className="h-8 w-8 mx-auto mb-3 opacity-50" />
            Henüz lead yok.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-mist/40 text-ink/65 text-[0.65rem] uppercase tracking-[0.18em] font-bold">
                <tr>
                  <th className="text-start px-5 py-3">Tarih</th>
                  <th className="text-start px-3 py-3">Ad / E-posta</th>
                  <th className="text-start px-3 py-3 hidden md:table-cell">Hizmet</th>
                  <th className="text-start px-3 py-3 hidden lg:table-cell">UTM</th>
                  <th className="text-start px-3 py-3">Mesaj</th>
                  <th className="text-end px-5 py-3">WA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-mist">
                {leads.map((l, idx) => {
                  const utm = [l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join("/") || "—";
                  return (
                    <tr key={l.id || `${l.email}-${l.created_at}-${idx}`} data-testid={`lead-row-${l.id || idx}`} className="hover:bg-mist/30">
                      <td className="px-5 py-3.5 text-xs text-ink/55 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-3 py-3.5">
                        <p className="font-semibold text-ink">{l.name}</p>
                        <p className="text-xs text-ink/55">{l.email} · {l.phone}</p>
                      </td>
                      <td className="px-3 py-3.5 hidden md:table-cell">
                        <span className="rounded-full bg-brand-pink/10 text-brand-pink-deep text-xs font-semibold px-2.5 py-1">{l.service}</span>
                      </td>
                      <td className="px-3 py-3.5 hidden lg:table-cell text-xs text-ink/55 font-mono">{utm}</td>
                      <td className="px-3 py-3.5 text-xs text-ink/65 max-w-xs truncate">{l.message || "—"}</td>
                      <td className="px-5 py-3.5 text-end">
                        <a href={`https://wa.me/${(l.phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Merhaba " + (l.name || "") + ", Yastık Medya'dan ulaşıyorum.")}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#25D366] text-white hover:bg-[#1FAB54] transition-colors"
                          data-testid={`lead-wa-${l.id}`} title="WhatsApp ile yanıtla">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
